import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, StateToken } from '@ngxs/store';
import { insertItem, patch, removeItem, updateItem } from '@ngxs/store/operators';
import { tap } from 'rxjs/operators';
import { AdminCompetenceRoutes } from 'src/app/core/enums/routes.enum';
import { ApiResponse } from 'src/app/core/models/api-response';
import { redirectAndBypassUnsavedDataCheck } from 'src/app/shared/functions/redirect-and-bypass-unsaved-data-check';
import { IdName } from 'src/app/shared/models/id-name';
import { ResetCurrentFilter } from 'src/app/core/state/main-table-filter.actions';
import { OnSuccess } from 'src/app/shared/decorators/on-success.decorator';
import { CompetenceApiService } from '../services/competence-api.service';
import {
  AddCompetence,
  DeleteCompetence,
  GetCompetenceById,
  LoadAllCompetences,
  LoadCompetences,
  LoadDepartmentsRelatedCompetences,
  UpdateCompetence,
  ClearCurrentCompetence,
} from './competence.actions';
import { ICompetenceModel } from '../model/competence.model';

export interface CompetenceStateModel {
  loaded: boolean;
  competences: ICompetenceModel[] | undefined;
  allCompetences: IdName[];
  departmentsRelatedCompetences: IdName[];
  competencesIsInUse: boolean;
  competencesTotalPages: number;
  currentCompetence: ICompetenceModel | undefined;
}

const COMPETENCE_STATE_TOKEN = new StateToken<CompetenceStateModel>('competenceState');

@State<CompetenceStateModel>({
  name: COMPETENCE_STATE_TOKEN,
  defaults: {
    loaded: false,
    competences: undefined,
    allCompetences: [],
    departmentsRelatedCompetences: [],
    competencesIsInUse: true,
    competencesTotalPages: 0,
    currentCompetence: undefined,
  },
})
@Injectable()
export class CompetenceState {
  constructor(private service: CompetenceApiService) {}

  @Action(LoadCompetences, { cancelUncompleted: true })
  loadCompetences(
    { getState, setState }: StateContext<CompetenceStateModel>,
    { isInUse, filter }: LoadCompetences
  ) {
    const isInUseChecker = isInUse ?? getState().competencesIsInUse;
    return this.service.getCompetences(isInUseChecker, filter).pipe(
      tap((response: ApiResponse<ICompetenceModel[]>) => {
        const state = getState();
        setState({
          ...state,
          loaded: true,
          competences: response.data,
          competencesTotalPages: response.totalPages,
          competencesIsInUse: isInUseChecker,
        });
      })
    );
  }

  @Action(LoadAllCompetences, { cancelUncompleted: true })
  loadAllCompetences(ctx: StateContext<CompetenceStateModel>) {
    return this.service.getAllCompetences().pipe(
      tap((allCompetences: IdName[]) => {
        ctx.setState(patch({ allCompetences }));
      })
    );
  }

  @Action(LoadDepartmentsRelatedCompetences, { cancelUncompleted: false })
  loadDepartmentsRelatedCompetences(
    ctx: StateContext<CompetenceStateModel>,
    { departmentIds, departmentGroupIds }: LoadDepartmentsRelatedCompetences
  ) {
    return this.service.getDepartmentsRelatedCompetences(departmentIds, departmentGroupIds).pipe(
      tap((departmentsRelatedCompetences: IdName[]) => {
        ctx.setState(patch({ departmentsRelatedCompetences }));
      })
    );
  }

  @Action(AddCompetence, { cancelUncompleted: true })
  @OnSuccess({ message: 'competenceAdded' })
  addCompetence(ctx: StateContext<CompetenceStateModel>, { competence }: AddCompetence) {
    return this.service.addCompetence(competence).pipe(
      tap((response: ICompetenceModel) => {
        ctx.setState(
          patch({
            competences: insertItem<ICompetenceModel>(response),
          })
        );
        redirectAndBypassUnsavedDataCheck(AdminCompetenceRoutes.Main, {
          queryParams: { index: '0' },
        });
      })
    );
  }

  @Action(GetCompetenceById, { cancelUncompleted: true })
  getCompetenceGroupById(ctx: StateContext<CompetenceStateModel>, { id }: GetCompetenceById) {
    // getting every existing competence from state
    const competenceGroupsFromState = ctx.getState().competences;
    // if a competence exist then just get updated (latest) from api
    if (competenceGroupsFromState?.find((cg: ICompetenceModel) => cg.id === id)) {
      return this.service.getCompetenceById(id).pipe(
        tap((response: ICompetenceModel) => {
          ctx.setState(
            patch({
              competences: updateItem<ICompetenceModel>(
                (toGet: ICompetenceModel | undefined) => toGet?.id === response.id,
                response
              ),
              currentCompetence: response,
            })
          );
        })
      );
    }
    // if it does not exist then fetch it from the api
    return this.service.getCompetenceById(id).pipe(
      tap((response: ICompetenceModel) => {
        ctx.setState(
          patch({
            competences: insertItem<ICompetenceModel>(response),
            currentCompetence: response,
          })
        );
      })
    );
  }

  @Action(UpdateCompetence, { cancelUncompleted: true })
  @OnSuccess({ message: 'competenceUpdated' })
  updateCompetence(ctx: StateContext<CompetenceStateModel>, { competence }: UpdateCompetence) {
    return this.service.updateCompetence(competence).pipe(
      tap(() => {
        redirectAndBypassUnsavedDataCheck(AdminCompetenceRoutes.Main, {
          queryParams: { index: '0' },
        });
      })
    );
  }

  @Action(DeleteCompetence, { cancelUncompleted: true })
  @OnSuccess({
    url: AdminCompetenceRoutes.Main,
    message: 'competenceDeleted',
    dispatch: ResetCurrentFilter,
  })
  deleteCompetence(ctx: StateContext<CompetenceStateModel>, { id }: DeleteCompetence) {
    return this.service.deleteCompetence(id).pipe(
      tap(() => {
        ctx.setState(
          patch({
            competences: removeItem<ICompetenceModel>((competence) => competence?.id === id),
          })
        );
      })
    );
  }

  @Action(ClearCurrentCompetence)
  clearCurrentCompetence(ctx: StateContext<CompetenceStateModel>) {
    ctx.setState(patch({ currentCompetence: undefined }));
  }

  @Selector()
  static competencesLoaded(state: CompetenceStateModel) {
    return state.loaded;
  }

  @Selector()
  static getCompetencesTotalPages(state: CompetenceStateModel) {
    return state.competencesTotalPages;
  }

  @Selector()
  static getCompetences(state: CompetenceStateModel) {
    return state.competences;
  }

  @Selector()
  static getAllCompetences(state: CompetenceStateModel): IdName[] {
    return state.allCompetences;
  }

  @Selector()
  static getDepartmentsRelatedCompetences(state: CompetenceStateModel): IdName[] {
    return state.departmentsRelatedCompetences;
  }

  @Selector()
  static getCompetenceById(state: CompetenceStateModel) {
    return (id: string) => {
      return state.competences?.find((competence: ICompetenceModel) => competence.id === id);
    };
  }

  @Selector()
  static getCurrentCompetence(state: CompetenceStateModel) {
    return state.currentCompetence;
  }
}
